import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Box,
  Button,
  Card,
  Heading,
  HStack,
  Progress,
  SimpleGrid,
  Spinner,
  Stack,
  Stat,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Tbody, Td, Th, Thead, Tr } from '@/components/ui/chakra-v2-compat';
import { toaster } from '@/components/ui/toaster';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { FaChartBar, FaRedo } from 'react-icons/fa';
import { bandasAPI } from '../services/api';

function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatMesLabel(mes) {
  if (!mes || typeof mes !== 'string') return '—';
  const [y, m] = mes.split('-');
  const monthNames = [
    'ene',
    'feb',
    'mar',
    'abr',
    'may',
    'jun',
    'jul',
    'ago',
    'sep',
    'oct',
    'nov',
    'dic',
  ];
  const idx = Number(m) - 1;
  if (!y || idx < 0 || idx > 11) return mes;
  return `${monthNames[idx]} ${String(y).slice(2)}`;
}

export default function BandaRepertorioEstadisticas({ active }) {
  
  const cardBg = useColorModeValue('white', 'gray.800');
  const panelBg = useColorModeValue('gray.50', 'gray.700');
  const borderCol = useColorModeValue('gray.200', 'gray.600');
  const barObras = useColorModeValue('#805AD5', '#B794F4');
  const barPaginas = useColorModeValue('#319795', '#4FD1C5');

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await bandasAPI.getObrasPartiturasEstadisticas();
      setData(res.data || null);
    } catch (e) {
      console.error(e);
      setData(null);
      toaster.create({
        title: 'No se pudieron cargar las estadísticas',
        type: 'error',
        duration: 4000,
        closable: true,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  const chartData = useMemo(() => {
    const rows = Array.isArray(data?.actividadSubida) ? data.actividadSubida : [];
    return rows.map((r) => ({
      ...r,
      label: formatMesLabel(r.mes),
    }));
  }, [data]);

  const resumen = data?.resumen;
  const etiquetado = data?.etiquetado;
  const top = Array.isArray(data?.topProgramadas) ? data.topProgramadas : [];
  const maxProg = top.length ? Math.max(...top.map((t) => t.programaciones || 0)) : 0;

  return (
    <Card.Root bg={cardBg} shadow="md" rounded="2xl" w="full" minW={0}>
      <Card.Header>
        <HStack justify="space-between" flexWrap="wrap" gap={3}>
          <HStack gap={2}>
            <FaChartBar />
            <Heading size="md">Estadísticas del archivo</Heading>
          </HStack>
          <Button size="sm" variant="outline" onClick={() => load()} loading={loading}><FaRedo />Actualizar
                      </Button>
        </HStack>
        <Text mt={2} fontSize="sm" color="gray.600">
          Resumen del Archivo Musical: obras, subidas, programación en actuaciones pasadas y
          cobertura de etiquetado. No incluye descargas de músicos (aún no se registran).
        </Text>
      </Card.Header>
      <Card.Body pt={0}>
        {loading && !data ? (
          <HStack py={10} justify="center" gap={3}>
            <Spinner size="sm" />
            <Text fontSize="sm" color="gray.500">
              Cargando estadísticas…
            </Text>
          </HStack>
        ) : !data ? (
          <Text fontSize="sm" color="gray.500" py={6}>
            No hay datos disponibles.
          </Text>
        ) : (
          <VStack align="stretch" gap={8}>
            <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} gap={3}>
              <Stat.Root px={3} py={3} rounded="xl" borderWidth="1px" borderColor={borderCol} bg={panelBg}>
                <Stat.Label fontSize="xs">Obras</Stat.Label>
                <Stat.ValueText fontSize="2xl">{resumen?.obrasTotal ?? 0}</Stat.ValueText>
              </Stat.Root>
              <Stat.Root px={3} py={3} rounded="xl" borderWidth="1px" borderColor={borderCol} bg={panelBg}>
                <Stat.Label fontSize="xs">% Listo</Stat.Label>
                <Stat.ValueText fontSize="2xl">{resumen?.pctListo ?? 0}%</Stat.ValueText>
                <Stat.HelpText mb={0} fontSize="xs">
                  {resumen?.obrasListo ?? 0} listo · {resumen?.obrasBorrador ?? 0} borrador
                  {(resumen?.obrasProcesando ?? 0) > 0
                    ? ` · ${resumen.obrasProcesando} procesando`
                    : ''}
                </Stat.HelpText>
              </Stat.Root>
              <Stat.Root px={3} py={3} rounded="xl" borderWidth="1px" borderColor={borderCol} bg={panelBg}>
                <Stat.Label fontSize="xs">Páginas</Stat.Label>
                <Stat.ValueText fontSize="2xl">{resumen?.paginasTotal ?? 0}</Stat.ValueText>
              </Stat.Root>
              <Stat.Root px={3} py={3} rounded="xl" borderWidth="1px" borderColor={borderCol} bg={panelBg}>
                <Stat.Label fontSize="xs">Carpetas</Stat.Label>
                <Stat.ValueText fontSize="2xl">{resumen?.carpetasTotal ?? 0}</Stat.ValueText>
              </Stat.Root>
              <Stat.Root px={3} py={3} rounded="xl" borderWidth="1px" borderColor={borderCol} bg={panelBg}>
                <Stat.Label fontSize="xs">% Etiquetadas</Stat.Label>
                <Stat.ValueText fontSize="2xl">{etiquetado?.pctEtiquetadas ?? 0}%</Stat.ValueText>
                <Stat.HelpText mb={0} fontSize="xs">
                  {etiquetado?.paginasEtiquetadas ?? 0} de {etiquetado?.paginasTotal ?? 0}
                </Stat.HelpText>
              </Stat.Root>
              <Stat.Root px={3} py={3} rounded="xl" borderWidth="1px" borderColor={borderCol} bg={panelBg}>
                <Stat.Label fontSize="xs">Almacenamiento</Stat.Label>
                <Stat.ValueText fontSize="lg">{formatBytes(data?.almacenamiento?.bytes)}</Stat.ValueText>
                <Stat.HelpText mb={0} fontSize="xs">
                  Uso total de la banda
                </Stat.HelpText>
              </Stat.Root>
            </SimpleGrid>

            <Box>
              <Heading size="sm" mb={1}>
                Actividad de subida (12 meses)
              </Heading>
              <Text fontSize="xs" color="gray.600" mb={3}>
                Obras y páginas creadas por mes según fecha de alta.
              </Text>
              <Box
                h={{ base: '240px', md: '280px' }}
                w="100%"
                minW={0}
                rounded="xl"
                borderWidth="1px"
                borderColor={borderCol}
                px={2}
                py={3}
              >
                {active ? (
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                    <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={36} />
                      <Tooltip
                        formatter={(value, name) => [
                          value,
                          name === 'obras' ? 'Obras' : 'Páginas',
                        ]}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.mes || ''}
                      />
                      <Legend
                        formatter={(value) => (value === 'obras' ? 'Obras' : 'Páginas')}
                      />
                      <Bar dataKey="obras" fill={barObras} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="paginas" fill={barPaginas} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : null}
              </Box>
            </Box>

            <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6}>
              <Box>
                <Heading size="sm" mb={1}>
                  Más programadas
                </Heading>
                <Text fontSize="xs" color="gray.600" mb={3}>
                  Actuaciones finalizadas cuyo repertorio incluye la obra (misma regla que el
                  histórico de la ficha).
                </Text>
                {top.length === 0 ? (
                  <Text fontSize="sm" color="gray.500">
                    Aún no hay obras programadas en actuaciones pasadas.
                  </Text>
                ) : (
                  <Stack gap={3}>
                    {top.map((row, idx) => (
                      <Box key={row.obraId}>
                        <HStack justify="space-between" mb={1} gap={2}>
                          <Text fontSize="sm" fontWeight="600" lineClamp={1}>
                            {idx + 1}. {row.titulo}
                          </Text>
                          <Text fontSize="sm" color="gray.600" flexShrink={0}>
                            {row.programaciones}
                          </Text>
                        </HStack>
                        <Progress.Root
                          value={maxProg > 0 ? (row.programaciones / maxProg) * 100 : 0}
                          size="sm"
                          colorPalette="purple"
                          rounded="full">
                          <Progress.Track>
                            <Progress.Range />
                          </Progress.Track>
                        </Progress.Root>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>

              <Box>
                <Heading size="sm" mb={1}>
                  Incidencias del enlace compartido
                </Heading>
                <Text fontSize="xs" color="gray.600" mb={3}>
                  Reportes desde el repertorio público (enlace de músicos).
                </Text>
                <SimpleGrid columns={3} gap={3} mb={4}>
                  <Stat.Root
                    px={3}
                    py={3}
                    rounded="xl"
                    borderWidth="1px"
                    borderColor={borderCol}
                    bg={panelBg}
                  >
                    <Stat.Label fontSize="xs">Total</Stat.Label>
                    <Stat.ValueText fontSize="xl">{data?.incidencias?.total ?? 0}</Stat.ValueText>
                  </Stat.Root>
                  <Stat.Root
                    px={3}
                    py={3}
                    rounded="xl"
                    borderWidth="1px"
                    borderColor={borderCol}
                    bg={panelBg}
                  >
                    <Stat.Label fontSize="xs">Pendientes</Stat.Label>
                    <Stat.ValueText fontSize="xl">{data?.incidencias?.pendientes ?? 0}</Stat.ValueText>
                  </Stat.Root>
                  <Stat.Root
                    px={3}
                    py={3}
                    rounded="xl"
                    borderWidth="1px"
                    borderColor={borderCol}
                    bg={panelBg}
                  >
                    <Stat.Label fontSize="xs">Resueltas</Stat.Label>
                    <Stat.ValueText fontSize="xl">{data?.incidencias?.resueltas ?? 0}</Stat.ValueText>
                  </Stat.Root>
                </SimpleGrid>
                <Box overflowX="auto" rounded="xl" borderWidth="1px" borderColor={borderCol}>
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>Métrica</Table.ColumnHeader>
                        <Table.ColumnHeader textAlign='end'>Valor</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      <Table.Row>
                        <Table.Cell>Páginas etiquetadas</Table.Cell>
                        <Table.Cell textAlign='end'>
                          {etiquetado?.paginasEtiquetadas ?? 0} / {etiquetado?.paginasTotal ?? 0}
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.Cell>Obras en listo</Table.Cell>
                        <Table.Cell textAlign='end'>
                          {resumen?.obrasListo ?? 0} / {resumen?.obrasTotal ?? 0}
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.Cell>Almacenamiento</Table.Cell>
                        <Table.Cell textAlign='end'>{formatBytes(data?.almacenamiento?.bytes)}</Table.Cell>
                      </Table.Row>
                    </Table.Body>
                  </Table.Root>
                </Box>
              </Box>
            </SimpleGrid>
          </VStack>
        )}
      </Card.Body>
    </Card.Root>
  );
}
